import { toast } from "react-toastify";
import {
  getPrinterSettings,
  isElectron,
  openPrinterHelper,
  printThermalReceipt,
} from "../renderer/platform";
import { APP_CONFIG } from "../utils/runtimeConfig";

const waitForPrintAssets = async (element) => {
  if (document.fonts?.ready) await document.fonts.ready;

  const images = Array.from(element.querySelectorAll("img"));
  await Promise.all(
    images.map(async (image) => {
      if (typeof image.decode !== "function") return;
      await image.decode().catch(() => undefined);
    })
  );
};

const canvasBlob = (canvas, mimeType, quality) =>
  new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error("Failed to create print image"))),
      mimeType,
      quality
    );
  });

const findPrintableElement = async (elementId, timeoutMs = 2500) => {
  const deadline = Date.now() + timeoutMs;
  do {
    const element = document.getElementById(elementId);
    if (element?.textContent?.trim() && element.querySelector("table")) return element;
    await new Promise((resolve) => requestAnimationFrame(resolve));
  } while (Date.now() < deadline);

  return null;
};

export default async function printInvoices(elementId) {
  const desktop = isElectron();
  const isKOT = elementId === "kot-print-section";
  const baseUrl = APP_CONFIG.apiBaseUrl;
  let clone = null;

  if (!desktop && !baseUrl) {
    toast.error("Printer not configured");
    return false;
  }

  try {
    const desktopSettings = desktop ? await getPrinterSettings() : null;
    if (desktop && !desktopSettings) {
      toast.error("Select a printer in Printer Settings first");
      return false;
    }

    const original = await findPrintableElement(elementId);
    if (!original) {
      toast.error(`Element "${elementId}" not found`);
      return false;
    }

    // The existing receipt DOM remains the single layout source. A detached clone is
    // rasterized so the native and browser print paths produce the same composition.
    clone = original.cloneNode(true);
    clone.dataset.orderlyPrintCapture = isKOT ? "kot" : "invoice";
    const style = document.createElement("style");

    style.textContent = isKOT
      ? `
        [data-orderly-print-capture="kot"] {
          width: 100%;
          font-size: 125%;
          padding: 10px 0;
          background: white;
        }
        [data-orderly-print-capture="kot"] * {
          margin: 0;
          padding: 2px 0;
          box-shadow: none;
          box-sizing: border-box;
        }
      `
      : `
        [data-orderly-print-capture="invoice"] {
          font-size: 100%;
          padding: 10px 0;
          background: white;
        }
        [data-orderly-print-capture="invoice"] * {
          margin: 0;
          padding: 2px 0;
          box-shadow: none;
        }
      `;

    clone.prepend(style);
    clone.style.position = "fixed";
    clone.style.left = "-10000px";
    clone.style.top = "0";
    clone.style.display = "block";
    clone.style.background = "white";
    clone.style.pointerEvents = "none";
    document.body.appendChild(clone);

    await new Promise((resolve) => requestAnimationFrame(resolve));
    await waitForPrintAssets(clone);

    const { default: html2canvas } = await import("html2canvas");
    const targetDots = desktopSettings?.paperWidth === "80mm" ? 576 : 384;
    const logicalWidth = desktopSettings?.paperWidth === "80mm" ? 230 : 180;
    clone.style.width = `${logicalWidth}px`;
    
    // Allow layout to update before capture
    await new Promise((resolve) => requestAnimationFrame(resolve));
    
    const layoutWidth = Math.max(clone.getBoundingClientRect().width, clone.scrollWidth, 1);
    const scale = desktop
      ? Math.min(4, Math.max(1, targetDots / layoutWidth))
      : undefined;
    const canvas = await html2canvas(clone, {
      backgroundColor: "#ffffff",
      useCORS: true,
      windowWidth: logicalWidth,
      windowHeight: clone.scrollHeight,
      logging: false,
      ...(scale ? { scale } : {}),
    });

    if (desktop) {
      const blob = await canvasBlob(canvas, "image/png");
      const result = await printThermalReceipt({
        kind: isKOT ? "kot" : "invoice",
        mimeType: "image/png",
        data: new Uint8Array(await blob.arrayBuffer()),
      });
      if (!result.submitted) {
        throw new Error(result.message || "The print job was not accepted");
      }

      toast.success(
        `🖨️ ${isKOT ? "KOT" : "Invoice"} sent to ${result.printerName || "printer"}`
      );
      return true;
    }

    const blob = await canvasBlob(canvas, "image/jpeg", 0.9);
    const formData = new FormData();
    formData.append("image", blob, isKOT ? "kot.jpg" : "invoice.jpg");

    const response = await fetch(`${baseUrl}/images/upload`, {
      method: "POST",
      body: formData,
    });
    if (!response.ok) throw new Error(await response.text());

    const data = await response.json();
    const token = data?.token;
    if (!token) throw new Error("Token not received from printer service");

    const opened = await openPrinterHelper(token);
    if (!opened) throw new Error("Printer helper is not installed or could not be opened");

    toast.success(`🖨️ ${isKOT ? "KOT" : "Invoice"} sent to printer`);
    return true;
  } catch (error) {
    console.error("Print error:", error);
    toast.error(error instanceof Error ? error.message : "Print failed");
    return false;
  } finally {
    clone?.remove();
  }
}
